
import React from "react";
import { CreditCard, Shield, AlertCircle } from "lucide-react";

const PaymentProcessingStep = ({
  installationData,
  selectedContractor,
  isProcessingPayment,
  paymentCardDetails,
  setPaymentCardDetails,
}) => {
  return (
    <div className="space-y-8 relative">
      {/* Payment Processing Overlay */}
      {isProcessingPayment && (
        <div className="absolute inset-0 bg-white bg-opacity-95 z-50 flex flex-col items-center justify-center rounded-3xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto mb-6"></div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Processing Payment...
            </h3>
            <p className="text-gray-600 mb-4">
              Please wait while we process your payment securely
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Shield className="w-4 h-4 text-emerald-600" />
              <span>Encrypted & Secure</span>
            </div>
          </div>
        </div>
      )}

      <div className="text-center">
        <CreditCard className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Complete Payment
        </h2>
        <p className="text-gray-600">
          {installationData.paymentMethod === "payment-plan"
            ? `Pay your 30% down payment of $${(
                selectedContractor.selectedBid.price * 0.3
              ).toLocaleString()}`
            : `Complete your full payment of $${selectedContractor.selectedBid.price.toLocaleString()}`}
        </p>
      </div>

      {/* Payment Amount Summary */}
      <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
        <div className="text-center">
          <div className="text-sm text-emerald-700 mb-2">
            {installationData.paymentMethod === "payment-plan"
              ? "30% Down Payment"
              : "Full Payment"}
          </div>
          <div className="text-4xl font-bold text-emerald-800 mb-4">
            $
            {installationData.paymentMethod === "payment-plan"
              ? (selectedContractor.selectedBid.price * 0.3).toLocaleString()
              : selectedContractor.selectedBid.price.toLocaleString()}
          </div>
          {installationData.paymentMethod === "payment-plan" && (
            <div className="text-sm text-emerald-600">
              Remaining balance: $
              {(selectedContractor.selectedBid.price * 0.7).toLocaleString()}{" "}
              (due on completion)
            </div>
          )}
        </div>
      </div>

      {/* Demo Card Info Notice */}
      <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <span className="font-semibold">Demo Mode:</span> Card details are
            pre-filled for testing. In production, customers would enter their
            real card information.
          </div>
        </div>
      </div>

      {/* Dummy Stripe Payment Form with Pre-filled Data */}
      <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Payment Information
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Card Number
            </label>
            <div className="relative">
              <input
                type="text"
                value={paymentCardDetails.cardNumber}
                onChange={(e) =>
                  setPaymentCardDetails({
                    ...paymentCardDetails,
                    cardNumber: e.target.value,
                  })
                }
                className="w-full p-4 border-2 border-emerald-300 bg-emerald-50 rounded-2xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all text-lg font-semibold"
                maxLength="19"
                readOnly
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex space-x-2">
                <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                  VISA
                </div>
                <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">
                  MC
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="text"
                value={paymentCardDetails.expiryDate}
                onChange={(e) =>
                  setPaymentCardDetails({
                    ...paymentCardDetails,
                    expiryDate: e.target.value,
                  })
                }
                className="w-full p-4 border-2 border-emerald-300 bg-emerald-50 rounded-2xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all text-lg font-semibold"
                maxLength="5"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                CVV
              </label>
              <input
                type="text"
                value={paymentCardDetails.cvv}
                onChange={(e) =>
                  setPaymentCardDetails({
                    ...paymentCardDetails,
                    cvv: e.target.value,
                  })
                }
                className="w-full p-4 border-2 border-emerald-300 bg-emerald-50 rounded-2xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all text-lg font-semibold"
                maxLength="4"
                readOnly
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cardholder Name
            </label>
            <input
              type="text"
              value={paymentCardDetails.cardholderName}
              onChange={(e) =>
                setPaymentCardDetails({
                  ...paymentCardDetails,
                  cardholderName: e.target.value,
                })
              }
              className="w-full p-4 border-2 border-emerald-300 bg-emerald-50 rounded-2xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all text-lg font-semibold"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Billing Address
            </label>
            <input
              type="text"
              value={paymentCardDetails.billingAddress}
              onChange={(e) =>
                setPaymentCardDetails({
                  ...paymentCardDetails,
                  billingAddress: e.target.value,
                })
              }
              className="w-full p-4 border-2 border-emerald-300 bg-emerald-50 rounded-2xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all text-lg font-semibold"
              readOnly
            />
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-200">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <div>
              <div className="font-semibold text-blue-800">Secure Payment</div>
              <div className="text-sm text-blue-600">
                Your payment information is encrypted and secure. This transaction
                is processed by Stripe.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessingStep;
